import { CircleMarker, PointTuple } from 'leaflet';
import { environment } from '../../environments/environment';
import { Layer } from 'leaflet';

export enum ReportStatus {
  InProgress = 'IN_PROGRESS',
  PlacmentOfHubDone = 'PLACEMENT_OF_HUB_DONE',
  Done = 'DONE',
  Deviation = 'DONE_WITH_DEVIATION',
  Unknown = 'UNKNOWN',
  DoneWithoutImages = 'DONE_WITHOUT_IMAGES',
}

const defintions = environment.constructionDefinitions;

export const ConstructionDone = defintions.ConstructionDone;

export enum PointType {
  Hp = defintions.Hp,
  Hc = defintions.Hc,
  CableWell = defintions.CableWell,
  Cabinet = defintions.Cabinet,
  JointBox = defintions.JointBox,
  JointPoint = defintions.JointPoint,
  SlingWell = defintions.SlingWell,
  DuctEndpoint = defintions.DuctEndpoint,
  DuctPoint = defintions.DuctPoint,
  Hub = defintions.Hub,
  Site = defintions.Site,
  MultiHP = -2,
  MultiHC = -3,
  MultiHouseHold = -4,
  MultiWell = -5,
}

export enum InformationPoint {
  Standard = defintions.StandardInfo,
  Trv = defintions.Trv,
  Lst = defintions.Lst,
  TrvShort = defintions.TrvShort,
  TrvLong = defintions.TrvLong,
  RailRoad = defintions.RailRoad,
  Water = defintions.Water,
  Misc = defintions.Misc,
  NotInUse = defintions.NotInUse,
  Information = defintions.Information,
}

export const DistPointLayerTypeMap = {
  HC: [PointType.Hc],
  HP: [PointType.Hp],
  WELL: [PointType.CableWell, PointType.SlingWell],
  MANHOLE: [PointType.CableWell, PointType.SlingWell],
  CABINET: [PointType.Cabinet, PointType.JointBox],
  JOINTPOINT: [PointType.JointPoint],
  SDP: [PointType.DuctEndpoint],
  MULTIHP: [-2],
  MULTIHC: [-3],
  MULTIHOUSEHOLD: [-4],
  MULTIWELL: [-5],
  HUB: [PointType.Hub],
  SITE: [PointType.Site],
  KP: [PointType.DuctPoint],
};

export const InfoPointLayerTypeMap = {
  INFO: [
    InformationPoint.Standard,
    InformationPoint.Trv,
    InformationPoint.Lst,
    InformationPoint.Information,
  ],
  TRV: [
    InformationPoint.TrvShort,
    InformationPoint.TrvLong,
    InformationPoint.RailRoad,
    InformationPoint.Water,
    InformationPoint.Misc,
    InformationPoint.NotInUse,
  ],
};

const MultiPointLayerTypeMap = {
  MULTIHP: -2,
  MULTIHC: -3,
  MULTIHOUSEHOLD: -4,
  MULTIWELL: -5,
};

export type PointLayerTypes =
  | keyof typeof InfoPointLayerTypeMap
  | keyof typeof DistPointLayerTypeMap
  | keyof typeof MultiPointLayerTypeMap;

export enum PopupTypes {
  Span,
  Info,
  DistPoint,
  Households,
  LandAgreement,
  PermitArea,
  MultiHP,
  MultiHC,
  MultiHouseHold,
  MultiWell,
  Site,
  Hub,
  Ductpoint,
}

export interface StatusReport {
  offline: boolean;
  status: ReportStatus;
}

export interface SpanProperties {
  spanId: string;
  lineStatusId: number;
  accommodationTypeId: number;
  accommodationMethodId: number;
  permissionTypeId: number;
  length: string;
  lineTypeId: number;
  serialNumber: string;
}

export interface Version {
  version: string;
  ifsNumber: string;
  publishedDate: string;
}

export interface InfoPointProperties {
  name: string;
  markerTypeId: number;
  description: string;
  freeText: string;
}

export interface DistributionPointProperties {
  id: string;
  name: string;
  markerTypeId: number;
  ductId: string;
  subductId: string;
  ductIds: string[];
  serialNumber: string;
  leadId: string;
  sourceKey: string;
  telematorId: string;
  netadminId?: string;
  existing?: boolean;
  coordinates?: { latitude: number; longitude: number };
  duct?: DuctProperties;
  subDuct?: DuctProperties;
  incomingDucts?: DuctProperties[];
  outgoingDucts?: DuctProperties[];
  aggregatedMarkerReferences?: DistributionPointProperties[];
  connectedToAddress?: string;
  linkedFtthMarkers?: LinkedFtthMarker[];
}

export interface LinkedFtthMarker {
  id: string;
  sourceKey: string;
  leadId: string;
}

export interface SelectedMapItem extends DistributionPointProperties {
  typeId: PopupTypes;
  clickedObject: CircleMarker;
  expandedPointId?: string;
}

export interface MultiHcPointProperties {
  geometry: { coordinates: number[] };
  id: string;
  properties: {
    id: string;
    tempId: string;
    name: string;
    pointType: string;
    subType: string;
    definitionTypeId: number;
    markerTypeId: number;
    telematorId: string;
    addressSource: string;
    ductId: string;
    subductId: string;
    ductIds: string[];
    preDocumented: boolean;
    networkType: number;
    networkTypeText: string;
    sourceKey: string | undefined;
  };
  type: string;
}

export interface MultiHpPointProperties extends MultiHcPointProperties {
  status: string;
}

export interface MultiWellPointProperties extends MultiHpPointProperties {}

export interface MultiPointData {
  hp: MultiHpPointProperties[];
  hc: MultiHcPointProperties[];
  well: MultiWellPointProperties[];
}

export interface BaseReportRequest {
  ifsNumber: string;
  buildSector: string;
  reportStatus: ReportStatus;
  deviationComment: string;
  reportComment: string;
  id?: string;
}

export interface DuctInfo {
  id: string;
  colorCode: string;
  name: string;
  serialNumber: string;
  number: number;
  colorName: string;
  ductName: string;
}

export interface SubDuct extends DuctInfo {
  cable?: {
    id: string;
    name: string;
  };
  ductNumber: number;
}

export interface DuctProperties extends DuctInfo {
  fromPointName: string;
  toPointName: string;
}

export interface Duct extends DuctInfo {
  colorName: string;
  spanIds: string[];
  subDucts: SubDuct[];
  fromPointId: string;
  fromPointName: string;
  toPointId: string;
  toPointName: string;
}

export interface DuctData {
  ductMap: { [key: string]: Duct };
  spanIdToDuctIdMap: { [key: string]: string[] };
}

export interface DuctMapping extends Duct {
  ductMapId: string;
}

export enum ExistingTypeEnum {
  NEW = 'NEW',
}

export interface InformationPointResponse {
  id: string;
  uid: string;
  alternateId: string;
  serialUid: number;
  isNew: boolean;
  name: string;
  desc: string;
  freeText: string;
  latitude: string;
  longitude: string;
  markerType: string;
  markerSubType: string;
  markerTypeId: number;
  markerSubTypeId: number;
  settings: number;
  projectCode: string;
  markerDuctUid: string;
  hardwareDuctSubDuctId: string;
  accessPointMarkerId: string;
  telematorId: string;
  address: string;
  addressWoCity: string;
  telematorIdExists: boolean;
  dataComplete: boolean;

  eventLength: number;
  autoSizeAdditionalFibers: number;
  manualDucts: boolean;
  markerSettings: [
    {
      id: string;
      uid: string;
      markerId: string;
      name: string;
      value: string;
      markerSettingType: number;
    },
  ];
  sectorId: string;
  existingType: ExistingTypeEnum;
  supplementaryOrder: boolean;
  markerDucts: [
    {
      id: string;
      uid: string;
      serialUid: number;
      colorName: boolean;
      markerId: string;
      name: string;
      hardwareDuctId: string;
      autoDuct: boolean;
    },
  ];
  netadminAddressId: string;
  netadminInstalled: boolean;
  addressMasterId: number;
  sourceKey: string;
  addressSource: string;
  buildingId: string;
  duplicateMarkerId: string;
  networkType: number;
  chamberType: string;
  cabinetType: string;
  placementType: string;
}
export enum PermitAreaStatus {
  CREATED = 'CREATED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  APPROVED = 'APPROVED',
  ADD = 'ADD',
  REVISED = 'REVISED',
  WORK_DONE = 'WORK_DONE',
  INSPECTION_COMPLETED = 'INSPECTION_COMPLETED',
  NOT_UTILIZED = 'NOT_UTILIZED',
  REVOKED = 'REVOKED',
  CLOSED_BY_TRV = 'CLOSED_BY_TRV',
  MISPLACEMENT = 'MISPLACEMENT',
  EXPIRED = 'EXPIRED',
  SITING_NOT_FILED = 'SITING_NOT_FILED',
  SITING_PERMIT_FILED = 'SITING_PERMIT_FILED',
  SITING_PERMIT_APPROVED = 'SITING_PERMIT_APPROVED',
  EXCAVATION_PERMIT_FILED = 'EXCAVATION_PERMIT_FILED',
  EXCAVATION_PERMIT_APPROVED = 'EXCAVATION_PERMIT_APPROVED',
}

export interface PermitAreaResponse {
  id: string;
  name: string;
  description: string;
  type: string;
  typeId: number;
  status: PermitAreaStatus;
  statusId: number;
  geometry: GeoJSON.Polygon;
}

export interface PermitAreaProperties {
  name: string;
  description: string;
  type: string;
  status: PermitAreaStatus;
}

export type PointLayer = Layer & {
  feature?: { properties?: DistributionPointProperties };
};

export interface LeadMarkerSelection {
  layer: PointLayer;
  linkedKp?: DistributionPointProperties;
}

export enum IconPaths {
  CABINET_DONE_WITH_DEVIATION = 'CABINET/DONE_WITH_DEVIATION',
  CABINET_UNKNOWN = 'CABINET/UNKNOWN',
  CABINET_IN_PROGRESS = 'CABINET/IN_PROGRESS',
  CABINET_DONE = 'CABINET/DONE',
  SITE_UNKNOWN = 'SITE/UNKNOWN',
  SITE_IN_PROGRESS = 'SITE/IN_PROGRESS',
  SITE_DONE = 'SITE/DONE',
  HUB_UNKNOWN = 'HUB/UNKNOWN',
  HUB_IN_PROGRESS = 'HUB/IN_PROGRESS',
  HUB_DONE = 'HUB/DONE',
  HP_DONE = 'HP/DONE',
  HP_DONE_WITHOUT_IMAGES = 'HP/DONE_WITHOUT_IMAGES',
  HP_UNKNOWN = 'HP/UNKNOWN',
  MULTIHP_UNKNOWN = 'MULTI/HP/UNKNOWN',
  MULTIHC_UNKNOWN = 'MULTI/HC/UNKNOWN',
  MULTIHOUSEHOLD_UNKNOWN = 'MULTI/HOUSEHOLD/UNKNOWN',
  MULTIWELL_UNKNOWN = 'MULTI/WELL/UNKNOWN',
  MANHOLE_UNKNOWN = 'MANHOLE/UNKNOWN',
  MANHOLE_IN_PROGRESS = 'MANHOLE/IN_PROGRESS',
  MANHOLE_DONE = 'MANHOLE/DONE',
  SDP_UNKNOWN = 'SDP/UNKNOWN',
  KP_UNKNOWN = 'KP/UNKNOWN',
  KP_DONE = 'KP/DONE',
}

export enum MapIconsStyles {
  SQUARE = 'SQUARE',
  RHOMBUS = 'RHOMBUS',
  SHEILD = 'SHEILD',
  MULTI = 'MULTI',
}

export const iconStyleSizes: Partial<
  Record<MapIconsStyles | 'DEFAULT', PointTuple>
> = {
  DEFAULT: [16, 16],
  RHOMBUS: [24, 24],
  MULTI: [20, 20],
};

export const iconTypeSizes = {
  hub: iconStyleSizes.RHOMBUS,
  sdp: iconStyleSizes.RHOMBUS,
  multi: iconStyleSizes.MULTI,
  default: iconStyleSizes.DEFAULT,
};
